import React from 'react';

import { useUser } from '@repo/hooks/useUser';
import { POLICIES } from '@/lib/Authorization';
import { gql } from '@apollo/client';
import { useApolloClient } from '@apollo/client/react';
import { TypeOf, z } from 'zod';
import { Form } from '@repo/ui/Form';
import { TextInputField } from '@repo/ui/Form';
import { Button } from '@repo/ui/Button';
import { registerInput, useZodForm } from '@repo/hooks/useZodForm';

import type { Mercury } from '@repo/types/Models';
import { getGraphQLErrorMessage } from '@repo/util/getGraphQLErrorMessage';

// FORM SCHEMA

const updateAccountContactFormSchema = z.object({
  firstName: z.string().min(1, { message: 'Please include a first name.' }),
  lastName: z.string().min(1, { message: 'Please include a last name.' }),
  email: z.string().email({ message: 'Please submit a valid email address.' }),
  phoneNumber: z.string().min(7, { message: 'Please submit a valid phone number.' }),
  title: z.string().optional(),
  mobilePhoneNumber: z
    .string()
    .min(7, { message: 'Please submit a valid mobile phone number.' })
    .optional()
    .or(z.literal('')),
});

type UpdateAccountContactFormParams = TypeOf<typeof updateAccountContactFormSchema>;

interface UpdateAccountContactFormProps {
  accountPrimaryContact: Mercury.Models.AccountPrimaryContact;
  refetch: () => void;
}

const UpdateAccountContactForm = ({
  accountPrimaryContact,
  refetch,
}: UpdateAccountContactFormProps) => {
  const user = useUser();

  const userCanUpdate = POLICIES['account_primary_contacts:update'](user);

  // Mutation
  const UPDATE_ACCOUNT_PRIMARY_CONTACT = gql`
    mutation UpdateAccountPrimaryContact($id: ID!, $input: UpdateAccountPrimaryContactInput!) {
      updateAccountPrimaryContact(id: $id, input: $input) {
        accountPrimaryContact {
          id
        }
      }
    }
  `;

  const client = useApolloClient();

  const onSubmit = async (input: UpdateAccountContactFormParams) => {
    try {
      await client.mutate({
        mutation: UPDATE_ACCOUNT_PRIMARY_CONTACT,
        variables: {
          input: {
            ...input,
            label: 'primary_contact',
          },
          id: accountPrimaryContact.id,
        },
      });
    } catch (error) {
      throw new Error(
        getGraphQLErrorMessage(error) ??
          'Error while trying to update account primary contact. Please try again later or contact support if error persists.'
      );
    }

    refetch();
  };
  const form = useZodForm({
    schema: updateAccountContactFormSchema,
    onSubmit,
    defaultValues: {
      firstName: accountPrimaryContact.firstName,
      lastName: accountPrimaryContact.lastName,
      email: accountPrimaryContact.email,
      phoneNumber: accountPrimaryContact.phoneNumber,
      title: accountPrimaryContact.title ?? '',
      mobilePhoneNumber: accountPrimaryContact.mobilePhoneNumber ?? '',
    },
  });

  return (
    <>
      <Form {...form} className="grid gap-6 grid-col-1 xs:grid-cols-2">
        <Form.Success>Account primary contact successfully updated.</Form.Success>
        <Form.Error autoClose={15000} />
        <TextInputField label="Title" disabled={!userCanUpdate} {...registerInput(form, 'title')} />
        <TextInputField
          label="First Name"
          disabled={!userCanUpdate}
          {...registerInput(form, 'firstName')}
        />
        <TextInputField
          label="Last Name"
          disabled={!userCanUpdate}
          {...registerInput(form, 'lastName')}
        />
        <TextInputField
          label="Email address"
          disabled={!userCanUpdate}
          {...registerInput(form, 'email')}
        />
        <TextInputField
          label="Phone number"
          disabled={!userCanUpdate}
          {...registerInput(form, 'phoneNumber')}
        />
        <TextInputField
          label="Mobile phone number"
          disabled={!userCanUpdate}
          {...registerInput(form, 'mobilePhoneNumber')}
        />

        <div className="w-full">
          <Button type="submit" disabled={!userCanUpdate}>
            Update Contact
          </Button>
        </div>
      </Form>
    </>
  );
};

export default UpdateAccountContactForm;
